/**
 * Owner Finance: GMV, маржа платформы/партнёров, комиссии платёжек, чистая прибыль.
 *
 * Цифры приходят из `ownerFinanceBreakdown(periodDays)` — это зеркало
 * inline `/admin → 💰 Финансы`. Тот же агрегатор `compute_finance_breakdown`,
 * поэтому значения совпадают между UI'ями.
 *
 * RUB-конверсия — через текущий CBR-курс из `fxRates()` (с фоллбэком 100 ₽/$).
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, X, TrendingUp, BarChart3, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { AdminWithdrawRequest } from '@/api/types';
import { getWebApp, haptic } from '@/lib/telegram';

export const Route = createFileRoute('/finance')({ component: AdminFinance });

type Period = '7' | '30' | '90' | 'all';
const PERIOD_TO_DAYS: Record<Period, number> = { '7': 7, '30': 30, '90': 90, all: 365 };
const PERIOD_LABEL: Record<Period, string> = { '7': '7 дней', '30': '30 дней', '90': '90 дней', all: 'Всё время' };

type PayoutTab = 'active' | 'completed' | 'rejected';
const PAYOUT_TAB_LABEL: Record<PayoutTab, string> = {
  active: 'Активные',
  completed: 'Выплачено',
  rejected: 'Отклонено',
};

function AdminFinance() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>('30');
  const [payoutTab, setPayoutTab] = useState<PayoutTab>('active');

  const { data: withdraws, isPending: withdrawsPending } = useQuery({
    queryKey: ['payouts-list', payoutTab],
    queryFn: () =>
      payoutTab === 'active'
        ? api.pendingWithdraws()
        : api.payoutHistory(payoutTab),
    placeholderData: keepPreviousData,
  });

  const { data: breakdown, isPending: bdPending } = useQuery({
    queryKey: ['ownerFinanceBreakdown', period],
    queryFn: () => api.ownerFinanceBreakdown(PERIOD_TO_DAYS[period]),
    placeholderData: keepPreviousData,
  });

  const { data: rates } = useQuery({
    queryKey: ['fxRates'],
    queryFn: () => api.fxRates(),
    placeholderData: keepPreviousData,
  });
  const usdToRub = useMemo(() => {
    const r = (rates ?? []).find((x) => x.code === 'RUB');
    return r?.perRub ?? 100;
  }, [rates]);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { mutate: decideWithdraw } = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.decideWithdraw(id, approve).then(() => ({ id, approve })),
    onSuccess: ({ id, approve }) => {
      // Одобрение оставляет заявку в «активных» (approved), отклонение — убирает.
      if (!approve) {
        qc.setQueryData<AdminWithdrawRequest[]>(['payouts-list', 'active'], (prev) =>
          (prev || []).filter((w) => w.id !== id),
        );
      }
      qc.invalidateQueries({ queryKey: ['payouts-list'] });
      haptic(approve ? 'success' : 'warning');
      toast.success(approve ? 'Заявка одобрена' : 'Заявка отклонена');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось');
    },
  });

  const exportCsv = useMutation({
    mutationFn: (kind: 'subscriptions' | 'partners' | 'pnl') => api.exportCsv(kind),
    onError: () => toast.error('Не удалось выгрузить CSV'),
  });

  const decide = (id: string, approve: boolean) => decideWithdraw({ id, approve });
  const fmt = (centsUsd: number) =>
    `${(((centsUsd * usdToRub) / 100) | 0).toLocaleString('ru')} ₽`;

  return (
    <motion.div initial={false} className="p-4 space-y-6 pb-24">
      <div className="px-2 pt-2 flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Финансы</h2>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
          1$ ≈ {usdToRub.toFixed(1)}₽
        </div>
      </div>

      <PeriodSwitcher value={period} onChange={setPeriod} />

      {/* CSV-выгрузки */}
      <div className="grid grid-cols-3 gap-2">
        {([
          ['subscriptions', 'Подписки'],
          ['partners', 'Партнёры'],
          ['pnl', 'P&L'],
        ] as const).map(([kind, label]) => (
          <button
            key={kind}
            type="button"
            disabled={exportCsv.isPending}
            onClick={() => exportCsv.mutate(kind)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-white/5 py-2.5 text-xs font-bold text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {bdPending && !breakdown ? (
        <Skeleton className="h-[260px] rounded-[24px]" />
      ) : (
        <Card className="overflow-hidden relative">
          <div className="absolute top-0 right-0 w-48 h-48 bg-primary/10 blur-[100px] -mr-24 -mt-24 pointer-events-none" />
          <CardContent className="p-6 relative z-10 space-y-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider">{PERIOD_LABEL[period]}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MetricRow
                label="Оборот"
                sub="GMV — что клиенты заплатили"
                value={fmt(breakdown?.gross_revenue_minor ?? 0)}
                tooltip="GMV (Gross Merchandise Value) — сколько клиенты заплатили партнёрам за подписки за период в розничных ценах."
              />
              <MetricRow
                label="Маржа платформы"
                sub="Наш доход после партнёров"
                value={fmt(breakdown?.platform_margin_minor ?? 0)}
                accent="primary"
                tooltip="Что мы зарабатываем после партнёрских отчислений (до комиссий платёжек)."
              />
              <MetricRow
                label="Маржа партнёров"
                sub="Их markup поверх нашей цены"
                value={fmt(breakdown?.partner_margin_minor ?? 0)}
                accent="muted"
                tooltip="Сколько партнёры заработали за период (retail − platform_price)."
              />
              <MetricRow
                label="Cost AdaptGroup"
                sub="Что мы должны провайдеру"
                value={fmt(breakdown?.adaptgroup_cost_minor ?? 0)}
                accent="muted"
                tooltip="Себестоимость AdaptGroup — сколько мы платим за подписки клиентов за период."
              />
              <MetricRow
                label="Комиссии платёжек"
                sub="CryptoBot / Lava / Heleket / LZT"
                value={`−${fmt(breakdown?.payment_fees_usd_minor ?? 0)}`}
                accent="muted"
                tooltip="Удержание платёжных провайдеров: amount × fee / gross_amount по каждой успешной оплате."
              />
              <MetricRow
                label="Чистая прибыль"
                sub="Маржа − комиссии"
                value={fmt(breakdown?.net_profit_minor ?? 0)}
                accent="green"
                tooltip="Финальный профит: маржа платформы минус комиссии платёжек."
              />
            </div>
          </CardContent>
        </Card>
      )}

      <ProvidersSection
        providers={breakdown?.providers ?? []}
        fmt={fmt}
        loading={bdPending && !breakdown}
      />

      <Link
        to="/cohorts"
        preload="intent"
        className="block group"
      >
        <Card className="border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card hover:border-primary/40 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Когорты</div>
              <div className="text-[11px] text-muted-foreground">
                Маржа по месяцу первой покупки × тарифу
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          </CardContent>
        </Card>
      </Link>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2">
          Заявки на вывод
        </h3>
        <div className="grid grid-cols-3 gap-1.5 bg-muted/40 p-1 rounded-xl mb-3">
          {(['active', 'completed', 'rejected'] as PayoutTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setPayoutTab(t)}
              className={`py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                payoutTab === t
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {PAYOUT_TAB_LABEL[t]}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {withdrawsPending && !withdraws ? (
            <>
              <Skeleton className="h-[120px] rounded-2xl" />
              <Skeleton className="h-[120px] rounded-2xl" />
            </>
          ) : (withdraws || []).length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                {payoutTab === 'active' ? 'Нет активных заявок' : 'Пусто'}
              </CardContent>
            </Card>
          ) : (
            withdraws?.map((w) => (
              <Card key={w.id} className="overflow-hidden">
                <Link
                  to="/withdrawals/$id"
                  params={{ id: w.id }}
                  preload="intent"
                  className="block active:bg-white/5 transition-colors"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold truncate">{w.partnerName}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(w.createdAt).toLocaleString('ru')} · {w.method}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="font-bold text-lg whitespace-nowrap">
                          {fmt(w.amountUsdMinor)}
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    {w.status && w.status !== 'pending' && (
                      <div className="mt-1">
                        <PayoutStatusChip status={w.status} />
                      </div>
                    )}
                  </CardContent>
                </Link>
                {payoutTab === 'active' && (
                  <div className="grid grid-cols-2 gap-2 px-4 pb-4">
                    <button
                      type="button"
                      onClick={() => decide(w.id, true)}
                      className="bg-green-500/15 text-green-500 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                      <Check className="w-4 h-4" />
                      Одобрить
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(w.id, false)}
                      className="bg-red-500/15 text-red-500 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                      <X className="w-4 h-4" />
                      Отклонить
                    </button>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>
    </motion.div>
  );
}

function PayoutStatusChip({ status }: { status: string }) {
  const cfg: Record<string, string> = {
    pending: 'bg-yellow-500/15 text-yellow-500',
    approved: 'bg-blue-500/15 text-blue-400',
    completed: 'bg-green-500/15 text-green-500',
    rejected: 'bg-red-500/15 text-red-500',
  };
  const label: Record<string, string> = {
    pending: 'ожидает',
    approved: 'одобрено',
    completed: 'выплачено',
    rejected: 'отклонено',
  };
  const cls = cfg[status] ?? 'bg-white/10 text-muted-foreground';
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cls}`}>
      {label[status] ?? status}
    </span>
  );
}

function PeriodSwitcher({ value, onChange }: { value: Period; onChange: (v: Period) => void }) {
  const opts: Period[] = ['7', '30', '90', 'all'];
  return (
    <div className="grid grid-cols-4 gap-1.5 bg-muted/40 p-1 rounded-xl">
      {opts.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={`py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
            value === p
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {PERIOD_LABEL[p]}
        </button>
      ))}
    </div>
  );
}

function ProvidersSection({
  providers,
  fmt,
  loading,
}: {
  providers: Array<{
    provider: string;
    paid_count: number;
    failed_count: number;
    revenue_usd_minor: number;
    fee_usd_minor: number;
  }>;
  fmt: (centsUsd: number) => string;
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-[140px] rounded-[24px]" />;

  const total = providers.reduce((s, p) => s + p.revenue_usd_minor, 0);

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2">
        Платёжки
      </h3>
      {providers.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Нет успешных оплат за период
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {providers.map((p) => {
            const share = total > 0 ? (p.revenue_usd_minor / total) * 100 : 0;
            const attempts = p.paid_count + p.failed_count;
            const success = attempts > 0 ? (p.paid_count / attempts) * 100 : 100;
            return (
              <Card key={p.provider}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="font-bold uppercase tracking-wider text-sm">
                      {p.provider}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {share.toFixed(0)}% оборота
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Выручка
                      </div>
                      <div className="font-bold">{fmt(p.revenue_usd_minor)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Комиссия
                      </div>
                      <div className="font-bold text-muted-foreground">
                        −{fmt(p.fee_usd_minor)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                    <span>{p.paid_count} оплат</span>
                    <span>{p.failed_count} failed</span>
                    <span className={success >= 95 ? 'text-green-500' : 'text-yellow-500'}>
                      {success.toFixed(0)}% success
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MetricRow({
  label,
  sub,
  value,
  accent,
  tooltip,
}: {
  label: string;
  sub?: string;
  value: string;
  accent?: 'primary' | 'green' | 'muted';
  tooltip?: string;
}) {
  const cls =
    accent === 'primary'
      ? 'text-primary'
      : accent === 'green'
        ? 'text-green-500'
        : accent === 'muted'
          ? 'text-muted-foreground'
          : '';
  return (
    <div title={tooltip} className="cursor-help">
      <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[9px] text-muted-foreground/70">{sub}</div>}
      <div className={`font-bold text-lg ${cls}`}>{value}</div>
    </div>
  );
}
